import React, { ReactElement } from "react";
import { enums, placeholder, widget } from "../utils";
import { WidgetProps } from "@rjsf/utils";
import CheckboxesWidget from "./CheckboxesWidget";
import RadioWidget from "./RadioWidget";
import Select, {
  SelectProps
} from "@amzn/awsui-components-react-v3/polaris/select";
import Multiselect, {
  MultiselectProps
} from "@amzn/awsui-components-react-v3/polaris/multiselect";
import { BaseSelectProps } from "@amzn/awsui-components-react-v3/polaris/select/interfaces";
import { NonCancelableCustomEvent } from "../types";

export default function SelectWidget(props: WidgetProps): ReactElement {
  const { enumOptions, enumDisabled } = enums(props);
  const enumValues = enumOptions.map(option => option.value);

  // RJSF defaults to select for non boolean cases, so route to the recommended
  // Polaris control: https://polaris.a2z.com/design/patterns/selection/
  // But honor an explicit `select` setting from `ui:widget`
  if (enumOptions.length < 8 && widget(props) !== "select") {
    return props.multiple ? (
      <CheckboxesWidget {...props} />
    ) : (
      <RadioWidget {...props} />
    );
  }

  const selectedValue = props.value as string; // set if `multiple === false`
  const selectedValues = props.value as string[]; // set if `multiple === true`

  // Handle single select and multiselect in one pass
  let selectedOption: SelectProps.Option | null = null;
  let selectedOptions: MultiselectProps.Option[] = [];
  const options: SelectProps.Option[] = enumOptions.map(({ value, label }) => {
    const option: SelectProps.Option = {
      disabled: enumDisabled?.includes(value),
      value,
      label: label || "-"
      // We should double check empty string handling per the documentation
      // https://react-jsonschema-form.readthedocs.io/en/latest/usage/validation/#the-case-of-empty-strings
    };
    if (!props.multiple && selectedValue === value) {
      selectedOption = option;
    }
    if (props.multiple && selectedValues.includes(value)) {
      selectedOptions.push(option);
    }
    return option;
  });

  const useFiltering = options.length > 15;
  const baseProps: BaseSelectProps = {
    id: props.id,
    ariaRequired: props.required,
    filteringType: useFiltering ? "auto" : "none",
    filteringAriaLabel: useFiltering ? "options" : undefined,
    disabled: props.disabled || props.readonly,
    onBlur: () => props.onBlur(props.id, null),
    onFocus: () => props.onFocus(props.id, null),
    placeholder: placeholder(props),
    selectedAriaLabel: "selected",
    noMatch: "No options"
  };
  // TODO: I18N?

  return props.multiple ? (
    <Multiselect
      {...baseProps}
      options={options}
      selectedOptions={selectedOptions}
      onChange={(
        e: NonCancelableCustomEvent<MultiselectProps.MultiselectChangeDetail>
      ) => {
        const newValues = e.detail.selectedOptions.map(option => option.value!);
        // We need to preserve the enum order though. For now, do quadratic sort
        newValues.sort((a, b) => enumValues.indexOf(a) - enumValues.indexOf(b));
        props.onChange(newValues);
      }}
    />
  ) : (
    <Select
      {...baseProps}
      options={options}
      selectedOption={selectedOption}
      onChange={(e: NonCancelableCustomEvent<SelectProps.ChangeDetail>) =>
        props.onChange(e.detail.selectedOption.value)
      }
    />
  );
}
